import { Quotes, Check, X, Image as ImageIcon, Envelope } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const GOAL_LABELS = {
  strength_muscle: "Strength/Muscle",
  weight_loss_composition: "Weight loss",
  confidence_gym: "Confidence",
  accountability_consistency: "Accountability",
  proper_form: "Form",
  nutrition_habits: "Nutrition",
  injury_limitation: "Injury/Limitation",
  general_health: "General health",
  other: "Other",
};

export default function TestimonialsPanel({ token, testimonials, setTestimonials, setError, onRefresh }) {
  const t = testimonials || null;

  const setTestimonialStatus = async (id, status) => {
    try {
      const r = await fetch(`${BACKEND}/api/admin/testimonials/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token },
        body: JSON.stringify({ status }),
      });
      if (r.ok) {
        setTestimonials((prev) => prev && {
          ...prev,
          testimonials: prev.testimonials.map((row) => row.id === id ? { ...row, status } : row),
          pending: prev.testimonials.filter((row) => (row.id === id ? status : row.status) === "pending").length,
        });
      }
    } catch (e) {
      console.error("testimonial status update failed:", e);
      setError(`Couldn't update testimonial: ${e?.message || "network error"}`);
    }
  };

  const badge = t && t.pending > 0
    ? <span className="text-[10px] uppercase tracking-[0.25em] bg-[#FF1B91] text-white px-2 py-1 font-bold">{t.pending} pending</span>
    : null;

  return (
    <CollapsibleSection
      id="testimonials"
      testId="admin-testimonials"
      title={<><Quotes size={18} weight="fill" className="text-[#FF1B91] inline mr-2" />Testimonials</>}
      subtitle={t ? `${t.total || 0} total` : "Loading…"}
      badge={badge}
      onRefresh={onRefresh}
    >
      {!t ? (
        <div className="text-white/50 text-sm">No testimonials loaded.</div>
      ) : (!t.testimonials || t.testimonials.length === 0) ? (
        <div className="border border-white/10 bg-[#0F0F0F] p-8 text-center text-white/40 text-sm">
          Nothing yet. Share <code className="text-[#FF1B91]">/testimonial</code> with happy clients.
        </div>
      ) : (
        <div className="space-y-3">
          {t.testimonials.map((row) => (
            <div key={row.id} data-testid={`testimonial-row-${row.id}`} className={`border ${row.status === "pending" ? "border-[#FF1B91]/60" : "border-white/10"} bg-[#0F0F0F] p-5`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white">
                    {row.display_name || row.first_name}
                    {row.location && <span className="text-white/40 font-normal"> · {row.location}</span>}
                    {row.duration && <span className="text-white/40 font-normal"> · {row.duration}</span>}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/35 mt-1 flex flex-wrap items-center gap-2">
                    <span>{fmtDate(row.created_at)}</span>
                    {row.email && <a href={`mailto:${row.email}`} className="inline-flex items-center gap-1 hover:text-[#FF1B91]"><Envelope size={11} /> {row.email}</a>}
                    <span className="text-white/25">display: {row.display_preference}</span>
                    {row.approval_preference === "yes_send_first" && <span className="text-[#FF1B91]">wants preview first</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] uppercase tracking-[0.25em] px-2 py-1 border ${row.status === "approved" ? "border-[#22C55E] text-[#22C55E]" : row.status === "rejected" ? "border-white/20 text-white/40" : "border-[#FF1B91] text-[#FF1B91]"}`}>{row.status}</span>
                  {row.status !== "approved" && (
                    <button data-testid={`approve-${row.id}`} onClick={() => setTestimonialStatus(row.id, "approved")} className="text-[10px] uppercase tracking-[0.2em] border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10 px-3 py-1.5 inline-flex items-center gap-1.5">
                      <Check size={12} weight="bold" /> Approve
                    </button>
                  )}
                  {row.status !== "rejected" && (
                    <button data-testid={`reject-${row.id}`} onClick={() => setTestimonialStatus(row.id, "rejected")} className="text-[10px] uppercase tracking-[0.2em] border border-white/20 text-white/60 hover:text-white hover:border-white/40 px-3 py-1.5 inline-flex items-center gap-1.5">
                      <X size={12} weight="bold" /> Reject
                    </button>
                  )}
                </div>
              </div>

              {row.goals && row.goals.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {row.goals.map((g) => (
                    <span key={g} className="text-[10px] uppercase tracking-[0.15em] border border-white/15 text-white/70 px-2 py-0.5">{GOAL_LABELS[g] || g}</span>
                  ))}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-[auto,1fr] items-start">
                {row.photo_url && (
                  <a href={`${BACKEND}${row.photo_url}`} target="_blank" rel="noreferrer noopener" data-testid={`testimonial-photo-${row.id}`} className="block border border-white/15 hover:border-[#FF1B91]">
                    <img src={`${BACKEND}${row.photo_url}`} alt="Submission" className="w-32 h-32 object-cover" />
                    <span className="block text-[9px] uppercase tracking-[0.2em] text-white/40 px-1 py-1 flex items-center gap-1"><ImageIcon size={10} /> {row.photo_permission ? "public ok" : "private"}</span>
                  </a>
                )}
                <div className="space-y-3 text-sm min-w-0">
                  <p className="text-white/90 italic leading-relaxed">&ldquo;{row.quote}&rdquo;</p>
                  {row.what_changed && <p className="text-white/70 leading-relaxed"><span className="text-[#FF1B91] text-[10px] uppercase tracking-[0.25em] font-bold block mb-1">What changed</span>{row.what_changed}</p>}
                  {row.recommendation && <p className="text-white/70 leading-relaxed"><span className="text-[#FF1B91] text-[10px] uppercase tracking-[0.25em] font-bold block mb-1">Would tell others</span>{row.recommendation}</p>}
                  {row.private_notes && <p className="text-yellow-300/80 leading-relaxed text-xs"><span className="text-yellow-500 text-[10px] uppercase tracking-[0.25em] font-bold block mb-1">Private note</span>{row.private_notes}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
